import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { trackSignupComplete, trackReferralSignup } from "../analytics";
import FloatingIcons, { SIGNUP_ICONS } from "../components/auth/FloatingIcons";
import {
  Box, Typography, TextField, Button, Link, Checkbox,
  FormControlLabel, InputAdornment, IconButton, Alert,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowForward, CheckCircle, ArrowBack } from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/common/LanguageSwitcher";
import { keyframes } from "@mui/material";

const BLUE       = "#1565C0";
const BLUE_MID   = "#1976D2";
const BLUE_LIGHT = "#42A5F5";
const NAVY       = "#0A1628";
const WHITE      = "#FFFFFF";
const TEXT_DARK  = "#0A1628";
const TEXT_MID   = "#37474F";
const TEXT_MUTED = "#78909C";
const BORDER     = "rgba(21,101,192,0.14)";

const cardEnter = keyframes`
  from { opacity:0; transform:translateY(44px) scale(.96); }
  to   { opacity:1; transform:translateY(0)    scale(1); }
`;
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
`;
const morphBg = keyframes`
  0%,100%{ border-radius:62% 38% 46% 54%/60% 44% 56% 40%; }
  33%    { border-radius:44% 56% 30% 70%/55% 30% 70% 45%; }
  66%    { border-radius:30% 70% 60% 40%/40% 62% 38% 60%; }
`;
const particleDrift = keyframes`
  0%  { transform:translate(0,0) rotate(0);   opacity:.5; }
  50% { transform:translate(20px,-38px) rotate(180deg); opacity:.9; }
  100%{ transform:translate(0,0) rotate(360deg); opacity:.5; }
`;
const shimmerBtn = keyframes`
  0%  { left:"-120%"; }
  100%{ left:"120%";  }
`;
const underlineDraw = keyframes`
  from{ width:0; } to{ width:100%; }
`;
const logoFloat = keyframes`
  0%,100%{ transform:translateY(0) rotate(0deg);  }
  50%    { transform:translateY(-8px) rotate(-4deg); }
`;
const successScale = keyframes`
  0%  { transform:scale(0) rotate(-20deg); opacity:0; }
  60% { transform:scale(1.18) rotate(6deg); opacity:1; }
  100%{ transform:scale(1) rotate(0); opacity:1; }
`;

const PARTICLES = [
  { w:6,  top:"6%",  left:"5%",  delay:"0s",   dur:"7s"   },
  { w:4,  top:"70%", left:"8%",  delay:"2s",   dur:"9s"   },
  { w:8,  top:"38%", left:"2%",  delay:"3.5s", dur:"6.5s" },
  { w:5,  top:"85%", left:"20%", delay:".8s",  dur:"8s"   },
  { w:7,  top:"14%", right:"6%", delay:"1.8s", dur:"7.5s" },
  { w:4,  top:"52%", right:"4%", delay:"4s",   dur:"8.5s" },
  { w:6,  top:"76%", right:"12%",delay:"1.3s", dur:"6s"   },
  { w:5,  top:"28%", right:"2%", delay:"3s",   dur:"9s"   },
];

const getStrength = (pw: string) => {
  let s = 0;
  if (pw.length >= 8)           s += 25;
  if (/[A-Z]/.test(pw))         s += 25;
  if (/[0-9]/.test(pw))         s += 25;
  if (/[^A-Za-z0-9]/.test(pw))  s += 25;
  return s;
};
type StrengthTier = "none" | "weak" | "fair" | "good" | "strong";

const strInfo = (s: number): { tier: StrengthTier; color: string } => {
  if (!s)    return { tier:"none",   color: BORDER   };
  if (s<=25) return { tier:"weak",   color:"#F44336" };
  if (s<=50) return { tier:"fair",   color:"#FF9800" };
  if (s<=75) return { tier:"good",   color:"#2196F3" };
  return           { tier:"strong", color:"#4CAF50" };
};

const fieldSx = (active: boolean) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: active ? WHITE : "#F4F8FF",
    transition: "background-color .25s ease",
    "& fieldset": {
      borderColor: active ? BLUE : BORDER,
      borderWidth: active ? "2px" : "1.5px",
      transition: "all .25s ease",
    },
    "&:hover fieldset":       { borderColor: BLUE_LIGHT },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
  "& .MuiInputBase-input": { color: TEXT_DARK, fontSize: ".88rem", py: "11px" },
});

const REF_STORAGE_KEY = "referral_code";

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const { t, isRTL } = useLanguage();
  const ts = t.auth.signup;
  // Layout mirroring comes from the RTL emotion cache; the arrow glyphs
  // themselves still have to be swapped by hand.
  const BackIcon    = isRTL ? ArrowForward : ArrowBack;
  const ForwardIcon = isRTL ? ArrowBack    : ArrowForward;

  const [showPw,    setShowPw]    = useState(false);
  const [showConf,  setShowConf]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [error,     setError]     = useState("");
  const [focus,     setFocus]     = useState("");
  const [mounted,   setMounted]   = useState(false);
  const [form, setForm] = useState({
    fullName:"", email:"", password:"", confirmPassword:"", agreeToTerms:false,
  });

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) sessionStorage.setItem(REF_STORAGE_KEY, ref);
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setForm(p => ({ ...p, [name]: type==="checkbox" ? checked : value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setError(ts.passwordMismatch); return; }
    if (!form.agreeToTerms) { setError(ts.mustAgree); return; }
    setLoading(true);
    try {
      const refCode = sessionStorage.getItem(REF_STORAGE_KEY) || undefined;
      const user = await authService.register({
        name: form.fullName, email: form.email,
        password: form.password, type: "user",
        ...(refCode ? { referredBy: refCode } : {}),
      });
      login(user);
      trackSignupComplete("email");
      if (refCode) {
        trackReferralSignup(refCode);
        sessionStorage.removeItem(REF_STORAGE_KEY);
      }
      setSuccess(true);
      setTimeout(() => navigate("/membership/Classes"), 1800);
    } catch (err: any) {
      setError(err.message || ts.failed);
    } finally { setLoading(false); }
  };

  const pwStr  = getStrength(form.password);
  const { tier: strTier, color: strColor } = strInfo(pwStr);
  const strLabel = strTier === "none" ? "" : ts.strength[strTier];
  const pwMatch = form.confirmPassword.length > 0 && form.password === form.confirmPassword;

  return (
    <>
    <Helmet>
      <title>{ts.metaTitle}</title>
      <meta name="description" content={ts.metaDescription} />
      <link rel="canonical" href="https://drpawleb.com/signup" />
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight:"100vh",
      background:`linear-gradient(145deg, ${NAVY} 0%, #0D2137 45%, #0A2040 100%)`,
      display:"flex", alignItems:"center", justifyContent:"center",
      position:"relative", overflow:"hidden", px:2, py:6,
    }}>

      {/* Grid */}
      <Box sx={{
        position:"absolute", inset:0, zIndex:0, opacity:.04,
        backgroundImage:`linear-gradient(rgba(255,255,255,.8) 1px,transparent 1px),
                         linear-gradient(90deg,rgba(255,255,255,.8) 1px,transparent 1px)`,
        backgroundSize:"52px 52px",
      }}/>

      {/* Orbs */}
      {[
        { top:"-10%", left:"-5%",  w:400, dur:"16s", c:"rgba(21,101,192,.18)"  },
        { top:"60%",  right:"-6%", w:320, dur:"21s", c:"rgba(66,165,245,.12)"  },
        { top:"25%",  left:"55%",  w:200, dur:"12s", c:"rgba(21,101,192,.09)"  },
      ].map((o,i) => (
        <Box key={i} sx={{
          position:"absolute", top:o.top, left:o.left, right:o.right,
          width:o.w, height:o.w, zIndex:0,
          background:`radial-gradient(circle,${o.c} 0%,transparent 70%)`,
          animation:`${morphBg} ${o.dur} ease-in-out infinite ${i%2?"reverse":""}`,
          animationDelay:`${i*1.8}s`,
        }}/>
      ))}

      {/* ── Back button ── */}
      <Button
        onClick={() => navigate("/")}
        startIcon={<BackIcon sx={{ fontSize: "1rem" }} />}
        sx={{
          position: "absolute", top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 },
          zIndex: 2, color: WHITE, textTransform: "none", fontWeight: 600,
          fontSize: ".88rem", borderRadius: "50px", px: 2.5, py: 1,
          border: "1px solid rgba(255,255,255,0.18)",
          backgroundColor: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(8px)",
          transition: "all 0.25s ease",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.35)" },
        }}
      >
        {t.auth.backHome}
      </Button>

      {/* ── Language switcher — mirrors to the opposite corner in RTL ── */}
      <Box sx={{ position: "absolute", top: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 }, zIndex: 2 }}>
        <LanguageSwitcher variant="light" />
      </Box>

      {/* ── Floating icons ── */}
      <FloatingIcons icons={SIGNUP_ICONS} />

      {/* ══ CARD ══ */}
      <Box sx={{
        position:"relative", zIndex:1,
        width:"100%", maxWidth:440,
        backgroundColor:WHITE,
        borderRadius:"28px",
        boxShadow:"0 32px 80px rgba(0,0,0,.45), 0 0 0 1px rgba(66,165,245,.12)",
        overflow:"hidden",
        animation: mounted ? `${cardEnter} .8s cubic-bezier(.22,1,.36,1) both` : "none",
      }}>

        {/* Accent bar */}
        <Box sx={{
          height:"4px",
          background:`linear-gradient(90deg,${BLUE},${BLUE_LIGHT},${BLUE})`,
          backgroundSize:"200% 100%",
          animation:"sb 3s linear infinite",
          "@keyframes sb":{ "0%":{ backgroundPosition:"-200% 0" }, "100%":{ backgroundPosition:"200% 0" } },
        }}/>

        <Box sx={{ px:{ xs:2.5, sm:4 }, py:{ xs:3, sm:3.5 } }}>

          {success ? (
            /* ── Success ── */
            <Box sx={{ textAlign:"center", py:6, animation:`${fadeUp} .6s ease both` }}>
              <Box sx={{
                width:90, height:90, borderRadius:"50%",
                background:"linear-gradient(135deg,#4CAF50,#2E7D32)",
                display:"flex", alignItems:"center", justifyContent:"center",
                mx:"auto", mb:3, fontSize:"2.6rem",
                boxShadow:"0 16px 48px rgba(76,175,80,.4)",
                animation:`${successScale} .7s cubic-bezier(.34,1.56,.64,1) both`,
              }}>✅</Box>
              <Typography sx={{ fontWeight:800, fontSize:"2rem", color:TEXT_DARK, mb:1 }}>
                {ts.successTitle}
              </Typography>
              <Typography sx={{ color:TEXT_MUTED }}>{ts.successSubtitle}</Typography>
            </Box>
          ) : (
            <>
              {/* Logo */}
              <Box
                onClick={() => navigate("/")}
                sx={{
                  display:"flex", alignItems:"center", gap:1,
                  cursor:"pointer", mb:2.5, width:"fit-content",
                  animation: mounted ? `${fadeUp} .6s ease .05s both` : "none",
                }}
              >
                <Box sx={{
                  width:36, height:36, borderRadius:"10px",
                  background:`linear-gradient(135deg,${BLUE},${BLUE_LIGHT})`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:"1.1rem",
                  boxShadow:`0 4px 14px rgba(21,101,192,.4)`,
                  animation:`${logoFloat} 4s ease-in-out infinite`,
                }}>🐾</Box>
                <Typography sx={{ fontWeight:800, fontSize:"1.25rem", color:NAVY, letterSpacing:"-.03em" }}>
                  Dr.<Box component="span" sx={{ color:BLUE }}>Paw</Box>
                </Typography>
              </Box>

              {/* Heading */}
              <Box sx={{ mb:2.5, animation: mounted ? `${fadeUp} .6s ease .12s both` : "none" }}>
                <Typography sx={{
                  fontWeight:800, fontSize:{ xs:"1.5rem", sm:"1.8rem" },
                  color:TEXT_DARK, letterSpacing:"-.025em", lineHeight:1.15, mb:.8,
                  position:"relative", display:"inline-block",
                  "&::after":{
                    content:'""', position:"absolute",
                    bottom:-5, left:0, height:"3px", borderRadius:"2px",
                    background:`linear-gradient(90deg,${BLUE},transparent)`,
                    animation: mounted ? `${underlineDraw} .7s ease .65s both` : "none",
                  },
                }}>
                  {ts.title}
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb:3, borderRadius:"12px", animation:`${fadeUp} .4s ease both` }}>
                  {error}
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit} noValidate
                sx={{ display:"flex", flexDirection:"column", gap:1.8 }}
              >
                {/* Full Name */}
                <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .18s both` : "none" }}>
                  <TextField
                    fullWidth label={ts.fullName} name="fullName"
                    value={form.fullName} onChange={handleChange} required
                    onFocus={() => setFocus("name")} onBlur={() => setFocus("")}
                    sx={fieldSx(focus==="name")}
                    InputProps={{ startAdornment:(
                      <InputAdornment position="start">
                        <Box sx={{ fontSize:".95rem", mr:.5, color: focus==="name"?BLUE:TEXT_MUTED }}>👤</Box>
                      </InputAdornment>
                    )}}
                  />
                </Box>

                {/* Email */}
                <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .22s both` : "none" }}>
                  <TextField
                    fullWidth label={ts.email} name="email" type="email"
                    value={form.email} onChange={handleChange} required
                    onFocus={() => setFocus("email")} onBlur={() => setFocus("")}
                    sx={fieldSx(focus==="email")}
                    InputProps={{ startAdornment:(
                      <InputAdornment position="start">
                        <Box sx={{ fontSize:".95rem", mr:.5, color: focus==="email"?BLUE:TEXT_MUTED }}>✉️</Box>
                      </InputAdornment>
                    )}}
                  />
                </Box>

                {/* Password + strength */}
                <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .26s both` : "none" }}>
                  <TextField
                    fullWidth label={ts.password} name="password"
                    type={showPw ? "text" : "password"}
                    value={form.password} onChange={handleChange} required
                    onFocus={() => setFocus("pw")} onBlur={() => setFocus("")}
                    sx={fieldSx(focus==="pw")}
                    InputProps={{
                      startAdornment:(
                        <InputAdornment position="start">
                          <Box sx={{ fontSize:".95rem", mr:.5, color: focus==="pw"?BLUE:TEXT_MUTED }}>🔒</Box>
                        </InputAdornment>
                      ),
                      endAdornment:(
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPw(!showPw)} size="small"
                            sx={{ color:TEXT_MUTED, "&:hover":{ color:BLUE } }}>
                            {showPw ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {form.password.length > 0 && (
                    <Box sx={{ mt:.9, animation:`${fadeUp} .3s ease both` }}>
                      <Box sx={{ display:"flex", justifyContent:"space-between", mb:.5 }}>
                        {[25,50,75,100].map(v => (
                          <Box key={v} sx={{
                            height:4, borderRadius:"4px", flex:1, mx:.3,
                            backgroundColor: pwStr >= v ? strColor : "rgba(0,0,0,.08)",
                            transition:"background-color .4s ease",
                          }}/>
                        ))}
                      </Box>
                      <Typography sx={{ fontSize:".72rem", fontWeight:700, color:strColor }}>
                        {strLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Confirm password */}
                <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .3s both` : "none" }}>
                  <TextField
                    fullWidth label={ts.confirmPassword} name="confirmPassword"
                    type={showConf ? "text" : "password"}
                    value={form.confirmPassword} onChange={handleChange} required
                    onFocus={() => setFocus("conf")} onBlur={() => setFocus("")}
                    sx={{
                      ...fieldSx(focus==="conf"),
                      ...(pwMatch ? { "& .MuiOutlinedInput-root fieldset":{ borderColor:"#4CAF50 !important", borderWidth:"2px !important" } } : {}),
                    }}
                    InputProps={{
                      startAdornment:(
                        <InputAdornment position="start">
                          <Box sx={{ fontSize:".95rem", mr:.5, color: focus==="conf"?BLUE:TEXT_MUTED }}>🔑</Box>
                        </InputAdornment>
                      ),
                      endAdornment: pwMatch ? (
                        <InputAdornment position="end">
                          <CheckCircle sx={{ color:"#4CAF50", fontSize:"1.15rem" }}/>
                        </InputAdornment>
                      ) : (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConf(!showConf)} size="small"
                            sx={{ color:TEXT_MUTED, "&:hover":{ color:BLUE } }}>
                            {showConf ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Terms */}
                <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .34s both` : "none" }}>
                  <FormControlLabel
                    control={
                      <Checkbox name="agreeToTerms" checked={form.agreeToTerms} onChange={handleChange}
                        size="small" sx={{ color:BORDER, "&.Mui-checked":{ color:BLUE } }}/>
                    }
                    label={
                      <Typography sx={{ fontSize:".84rem", color:TEXT_MID }}>
                        {ts.agreePrefix}{" "}
                        <Link component={RouterLink} to="/terms"
                          sx={{ color:BLUE, fontWeight:700, textDecoration:"none", "&:hover":{ textDecoration:"underline" } }}>
                          {ts.terms}
                        </Link>{" "}
                        {ts.and}{" "}
                        <Link component={RouterLink} to="/privacy"
                          sx={{ color:BLUE, fontWeight:700, textDecoration:"none", "&:hover":{ textDecoration:"underline" } }}>
                          {ts.privacy}
                        </Link>
                      </Typography>
                    }
                  />
                </Box>

                {/* Submit */}
                <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .38s both` : "none" }}>
                  <Button
                    fullWidth type="submit" variant="contained" disabled={loading}
                    sx={{
                      py:1.5, borderRadius:"14px", fontSize:".95rem", fontWeight:800,
                      textTransform:"none", letterSpacing:".01em",
                      background:`linear-gradient(135deg,${BLUE},${BLUE_MID})`,
                      boxShadow:`0 8px 24px rgba(21,101,192,.35)`,
                      position:"relative", overflow:"hidden",
                      "&::before":{
                        content:'""', position:"absolute",
                        top:0, width:"60%", height:"100%",
                        background:"linear-gradient(90deg,transparent,rgba(255,255,255,.22),transparent)",
                        animation:`${shimmerBtn} 2.5s ease-in-out infinite 1.2s`,
                      },
                      "&:hover":{ transform:"translateY(-2px)", boxShadow:`0 14px 36px rgba(21,101,192,.45)` },
                      "&:active":{ transform:"translateY(0)" },
                      transition:"all .3s cubic-bezier(.34,1.56,.64,1)",
                    }}
                  >
                    {loading
                      ? <CircularProgress size={22} sx={{ color:WHITE }}/>
                      : <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                          {ts.createAccount} <ForwardIcon sx={{ fontSize:"1.1rem" }}/>
                        </Box>
                    }
                  </Button>
                </Box>

                {/* Trust badges */}
                <Box sx={{
                  display:"flex", justifyContent:"center", gap:3, flexWrap:"wrap",
                  animation: mounted ? `${fadeUp} .6s ease .44s both` : "none",
                }}>
                  {[`🔒 ${ts.badges.secure}`, `🆓 ${ts.badges.free}`, `❌ ${ts.badges.noCard}`].map((badge,i) => (
                    <Typography key={i} sx={{ fontSize:".75rem", color:TEXT_MUTED, fontWeight:600 }}>{badge}</Typography>
                  ))}
                </Box>

              </Box>

              {/* Sign-in link */}
              <Box sx={{
                mt:2.5, textAlign:"center",
                animation: mounted ? `${fadeUp} .6s ease .48s both` : "none",
              }}>
                <Typography sx={{ color:TEXT_MUTED, fontSize:".9rem" }}>
                  {ts.haveAccount}{" "}
                  <Link component={RouterLink} to="/login" sx={{
                    color:BLUE, fontWeight:800, textDecoration:"none",
                    "&:hover":{ color:NAVY, textDecoration:"underline" },
                  }}>
                    {ts.signIn}
                  </Link>
                </Typography>
              </Box>
            </>
          )}

        </Box>
      </Box>
    </Box>
    </>
  );
};

export default Signup;
